import Button from '@hellodarwin/core/lib/components/common/button';
import Modal from '@hellodarwin/core/lib/components/common/hd-modal';
import Select from '@hellodarwin/core/lib/components/common/select';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import FormItem from '@hellodarwin/core/lib/components/common/form-item';
import FormLabel from '@hellodarwin/core/lib/components/common/form-label';
import { isNPO } from '@hellodarwin/core/lib/features/entities/companies-entities';
import { Industry } from '@hellodarwin/core/lib/features/entities/tags-entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { DatePicker } from 'antd';
import Form from 'antd/es/form';
import { useForm } from 'antd/es/form/Form';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import {
  closeCompleteProfileModal,
  selectCompleteProfileModal,
} from '../../features/api/slices/global-slice';
import {
  fetchNPOScopes,
  selectNPOScopes,
} from '../../features/api/slices/grants-slice';
import {
  fetchCompany,
  selectCompany,
  updateCompany,
} from '../../features/api/slices/profile-slice';
import {
  fetchIndustries,
  selectIndustriesSubsectors,
} from '../../features/api/slices/tags-slice';
import {
  fetchGoalTags,
  selectSearchableGoalTags,
} from '../../features/api/slices/tags-slice-v2';
import {
  useClientApi,
  useNewClientApi,
} from '../../features/api/use-client-api';
import {
  buildCompleteProfileModalFormValues,
  type ClientCompleteProfileModalForm,
} from './form-values';
import { StyledForm } from './styles';
import {
  CompleteProfileModalActions,
  CompleteProfileModalContainer,
} from './styles';

const ClientCompleteProfileModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const api = useClientApi();
  const newApi = useNewClientApi();
  const { t } = useTranslations();
  const [form] = useForm<ClientCompleteProfileModalForm>();

  const company = useAppSelector(selectCompany);
  const completeProfileModal = useAppSelector(selectCompleteProfileModal);
  const { selectedLocale } = useLocale();
  const isNonProfit = useMemo(
    () => isNPO(company.legal_structure ?? ''),
    [company.legal_structure],
  );

  const projectGoalsOptions = useAppSelector(selectSearchableGoalTags);
  const npoScopesOptions = useAppSelector((state) =>
    selectNPOScopes(state, selectedLocale),
  );
  const industrySubsectors = useAppSelector(selectIndustriesSubsectors);

  const [filteredSubsectorsOptions, setFilteredSubsectorsOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (
      isNonProfit ||
      !industrySubsectors.length ||
      !company.industry_sector?.length
    )
      return;

    const newFilteredSubsectors = industrySubsectors
      .filter((item: Industry) =>
        (company.industry_sector ?? []).includes(item.parent ?? ''),
      )
      .map((item) => ({
        label: `${item.id} - ${item.class_title}`,
        value: item.id,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));

    setFilteredSubsectorsOptions(newFilteredSubsectors);
  }, [isNonProfit, company.industry_sector, industrySubsectors]);

  const initialFormValues = useMemo(
    () => buildCompleteProfileModalFormValues(company),
    [company],
  );

  useEffect(() => {
    if (!completeProfileModal.isOpen) return;

    dispatch(fetchCompany({ api }));
    // Fetch project goals
    dispatch(
      fetchGoalTags({
        api: newApi,
        locale: selectedLocale,
      }),
    );
  }, [api, completeProfileModal.isOpen, dispatch, newApi, selectedLocale]);

  useEffect(() => {
    if (!completeProfileModal.isOpen) return;

    isNonProfit
      ? dispatch(fetchNPOScopes({ api: newApi }))
      : dispatch(fetchIndustries({ api, locale: selectedLocale }));
  }, [
    api,
    completeProfileModal.isOpen,
    dispatch,
    isNonProfit,
    newApi,
    selectedLocale,
  ]);

  useEffect(() => {
    if (!completeProfileModal.isOpen) return;

    form.setFieldsValue(initialFormValues);
  }, [completeProfileModal.isOpen, form, initialFormValues]);

  // Define handlers with useCallback to stabilize references
  const handleClose = useCallback(() => {
    dispatch(closeCompleteProfileModal());
  }, [dispatch]);

  const handleFinish = useCallback(
    async (values: any) => {
      const data = new FormData();
      data.append('request', JSON.stringify(values));
      await dispatch(
        updateCompany({ api: api, data, completeCompanyTourStep: true }),
      );
      dispatch(closeCompleteProfileModal());
      form.resetFields();
    },
    [dispatch, api, form],
  );

  // Calculate current title and subtitle
  const currentTitle =
    completeProfileModal.context === 'assistant'
      ? t('onboardingClient|charlesIncompleteProfileTitle')
      : t('onboardingClient|fundingExplorerIncompleteProfileTitle');

  const currentSubTitle =
    completeProfileModal.context === 'assistant'
      ? t('onboardingClient|charlesIncompleteProfileSubtitle')
      : t('onboardingClient|fundingExplorerIncompleteProfileSubtitle');

  return (
    <Modal
      open={completeProfileModal.isOpen}
      handleCancel={handleClose}
      header={
        <CompleteProfileModalContainer>
          <Typography elementTheme="h4" center>
            {currentTitle}
          </Typography>
          <Typography elementTheme="body1" center>
            {currentSubTitle}
          </Typography>
        </CompleteProfileModalContainer>
      }
      footer={
        <CompleteProfileModalActions>
          <Button
            variant="card-action"
            onClick={() => form.submit()}
            withArrowRight
          >
            {t('onboardingClient|completeProfile')}
          </Button>
        </CompleteProfileModalActions>
      }
    >
      <StyledForm>
        <Form
          form={form}
          name="profile"
          onFinish={handleFinish}
          layout="vertical"
        >
          <FormItem
            name="company_tag_goals"
            style={{ flex: 1 }}
            label={<FormLabel label={t('onboardingClient|project_goals')} />}
            rules={[
              {
                required: true,
                message: t('onboardingClient|projectGoalsRequired'),
              },
            ]}
          >
            <Select
              mode="multiple"
              size={'large'}
              options={projectGoalsOptions}
              placeholder={t('onboardingClient|projectGoalsPlaceholder')}
            />
          </FormItem>
          <Suspense fallback={<></>}>
            <FormItem
              name="founded_at"
              label={
                <FormLabel
                  label={t('profile|profileForm.company.labels.foundedAt')}
                />
              }
              style={{ flex: 1 }}
            >
              <DatePicker
                size="large"
                picker="year"
                allowClear
                style={{ width: '100%' }}
              />
            </FormItem>
          </Suspense>
          {isNonProfit ? (
            <FormItem
              name="npo_scopes_id"
              style={{ flex: 1 }}
              label={<FormLabel label={t('onboardingClient|npoScopes')} />}
              rules={[
                {
                  type: 'array',
                  max: 3,
                  message: t('onboardingClient|npoScopes_max_limit', {
                    limit: 3,
                  }),
                },
              ]}
            >
              <Select
                mode="multiple"
                size={'large'}
                options={npoScopesOptions}
                placeholder={t('onboardingClient|npoScopes')}
              />
            </FormItem>
          ) : (
            <FormItem
              name="industry_subsector"
              style={{ flex: 1 }}
              label={
                <FormLabel
                  label={t(
                    'profile|profileForm.company.labels.industrySubsector',
                  )}
                />
              }
            >
              <Select
                mode="multiple"
                size={'large'}
                options={filteredSubsectorsOptions}
                placeholder={t(
                  'profile|profileForm.company.placeholder.industrySubsector',
                )}
                notFoundContent={t(
                  'profile|profileForm.company.extra.industrySubsector',
                )}
              />
            </FormItem>
          )}
        </Form>
      </StyledForm>
    </Modal>
  );
};

export default ClientCompleteProfileModal;
